import React, { useId } from 'react';
import type { ConsoleStall } from '../api/client';

export type StallPickerViewMode = 'cards' | 'checkboxes' | 'dropdown';
export type StallPickerSortMode = 'name' | 'activity' | 'status';

export const STALL_PICKER_VIEW_MODES: StallPickerViewMode[] = ['cards', 'checkboxes', 'dropdown'];
export const STALL_PICKER_SORT_MODES: StallPickerSortMode[] = ['name', 'activity', 'status'];

const VIEW_MODE_LABEL: Record<StallPickerViewMode, string> = {
  cards: 'Cards',
  checkboxes: 'List',
  dropdown: 'Dropdown',
};

const SORT_MODE_LABEL: Record<StallPickerSortMode, string> = {
  name: 'Name (A–Z)',
  activity: 'Most hired',
  status: 'Live first',
};

interface AgentLite {
  agent_id: string;
  display_name: string;
}

interface StallPickerProps {
  stalls: ConsoleStall[];
  agents: AgentLite[];
  value: string;
  onChange: (stallId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  placeholder?: string;
  viewMode: StallPickerViewMode;
  setViewMode: (mode: StallPickerViewMode) => void;
  sortMode: StallPickerSortMode;
  setSortMode: (mode: StallPickerSortMode) => void;
}

function formatBalance(amount: number | string | null | undefined): string {
  const n = typeof amount === 'number' ? amount : Number(amount ?? 0);
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

function sortStalls(stalls: ConsoleStall[], mode: StallPickerSortMode): ConsoleStall[] {
  const sorted = stalls.slice();
  switch (mode) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'activity':
      sorted.sort((a, b) => {
        const ah = a.dex_stats?.hire_count ?? 0;
        const bh = b.dex_stats?.hire_count ?? 0;
        if (ah !== bh) return bh - ah;
        const aj = a.dex_stats?.job_count ?? 0;
        const bj = b.dex_stats?.job_count ?? 0;
        if (aj !== bj) return bj - aj;
        return a.name.localeCompare(b.name);
      });
      break;
    case 'status':
      sorted.sort((a, b) => {
        if (a.is_live !== b.is_live) return a.is_live ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      break;
  }
  return sorted;
}

const StallPicker: React.FC<StallPickerProps> = ({
  stalls,
  agents,
  value,
  onChange,
  disabled,
  loading,
  required,
  placeholder = '— pick a stall —',
  viewMode,
  setViewMode,
  sortMode,
  setSortMode,
}) => {
  const radioGroupName = useId();
  const sorted = sortStalls(stalls, sortMode);
  const isEmpty = stalls.length === 0;

  const workerName = (s: ConsoleStall): string | null => {
    if (!s.agent_id) return null;
    return agents.find((a) => a.agent_id === s.agent_id)?.display_name ?? null;
  };

  const renderEmpty = () => (
    <div className="stall-picker-empty">
      {loading ? 'Loading stalls…' : 'No stalls available.'}
    </div>
  );

  return (
    <div className="stall-picker">
      <div className="stall-picker-toolbar">
        <div className="stall-picker-toolbar-group" role="group" aria-label="Stall picker view mode">
          {STALL_PICKER_VIEW_MODES.map((m) => (
            <button
              key={m}
              type="button"
              className={`stall-picker-mode${viewMode === m ? ' active' : ''}`}
              onClick={() => setViewMode(m)}
              aria-pressed={viewMode === m}
              disabled={disabled}
            >
              {VIEW_MODE_LABEL[m]}
            </button>
          ))}
        </div>
        {viewMode !== 'dropdown' && stalls.length > 1 && (
          <label className="stall-picker-sort">
            <span>Sort</span>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as StallPickerSortMode)}
              disabled={disabled}
            >
              {STALL_PICKER_SORT_MODES.map((s) => (
                <option key={s} value={s}>{SORT_MODE_LABEL[s]}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {viewMode === 'dropdown' && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          required={required}
        >
          <option value="">{placeholder}</option>
          {sorted.map((s) => (
            <option key={s.stall_id} value={s.stall_id}>
              {s.name}{s.is_live ? '' : ' (offline)'}
            </option>
          ))}
        </select>
      )}

      {viewMode === 'cards' && (isEmpty ? renderEmpty() : (
        <div className="stall-picker-grid" role="radiogroup" aria-required={required}>
          {sorted.map((s) => {
            const selected = s.stall_id === value;
            const worker = workerName(s);
            return (
              <button
                key={s.stall_id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`stall-picker-card${selected ? ' selected' : ''}${s.is_live ? '' : ' offline'}`}
                onClick={() => { if (!disabled) onChange(s.stall_id); }}
                disabled={disabled}
              >
                <div className="stall-picker-card-title">{s.name}</div>
                <div className="stall-badges">
                  {s.is_live
                    ? <span className="stall-badge live">Live</span>
                    : <span className="stall-badge offline">Offline</span>}
                  {s.is_certified && <span className="stall-badge certified">Certified</span>}
                </div>
                <div className="stall-picker-card-worker">
                  {worker
                    ? <>worker: <strong>{worker}</strong></>
                    : <em>unassigned</em>}
                </div>
                <div className="stall-stats">
                  ${formatBalance(s.wallet_balance)} · {s.dex_stats?.job_count ?? 0} jobs · {s.dex_stats?.hire_count ?? 0} hires
                </div>
              </button>
            );
          })}
        </div>
      ))}

      {viewMode === 'checkboxes' && (isEmpty ? renderEmpty() : (
        <div className="stall-picker-list" role="radiogroup" aria-required={required}>
          {sorted.map((s) => {
            const selected = s.stall_id === value;
            const worker = workerName(s);
            return (
              <label
                key={s.stall_id}
                className={`stall-picker-row${selected ? ' selected' : ''}${s.is_live ? '' : ' offline'}`}
              >
                <input
                  type="radio"
                  name={radioGroupName}
                  checked={selected}
                  onChange={() => onChange(s.stall_id)}
                  disabled={disabled}
                />
                <span className="stall-picker-row-main">
                  <span className="stall-picker-row-name">
                    <span>{s.name}</span>
                    {!s.is_live && <span className="stall-badge offline">Offline</span>}
                    {s.is_certified && <span className="stall-badge certified">Certified</span>}
                  </span>
                  <span className="stall-picker-row-meta">
                    {worker ? <>worker: {worker} · </> : null}
                    {s.dex_stats?.hire_count ?? 0} hires · ${formatBalance(s.wallet_balance)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default StallPicker;
